import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Shield, Search, Eye, Edit, Trash2, MoreVertical,
  Check, Clock, Building2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import CommonToaster from "../Common/CommonToaster";
import { PageHeader, StatsCard, ViewToggle, StatusBadge, UserAvatar, EmptyState, AppCard, AppCardContent, AppButton, ConfirmModal } from "../ui";
import { TOTAL_PERMISSIONS, ROLE_OPTIONS, DEPARTMENT_OPTIONS } from "../../data/permissionCategories";
import OverLimitBanner from "../Common/OverLimitBanner";
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from "../../hooks/usePlanUsage";
import "./admins.scss";

/* ── helpers ── */
function formatLastLogin(dateStr) {
  if (!dateStr) return "Never";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

function getRoleBadgeClass(role) {
  switch (role) {
    case "Super Admin": return "role-badge--super-admin";
    case "Moderator": return "role-badge--moderator";
    default: return "role-badge--admin";
  }
}

/* ── Dropdown menu ── */
function AdminMenu({ admin, onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="admin-menu" ref={ref}>
      <button className="admin-menu__trigger" onClick={() => setOpen(!open)}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="admin-menu__dropdown">
          <button className="admin-menu__item" onClick={() => { setOpen(false); onView(admin); }}>
            <Eye size={14} /> View Profile
          </button>
          <button className="admin-menu__item" onClick={() => { setOpen(false); onEdit(admin); }}>
            <Edit size={14} /> Edit
          </button>
          <button className="admin-menu__item admin-menu__item--danger" onClick={() => { setOpen(false); onDelete(admin); }}>
            <Trash2 size={14} /> Remove
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Admin Card (grid view) ── */
function AdminCard({ admin, onView, onEdit, onDelete }) {
  const grantedPerms = (admin.permissions || []).length;

  return (
    <div className="admin-card">
      <div className="admin-card__header">
        <div className="admin-card__user">
          <UserAvatar name={`${admin.firstName} ${admin.lastName}`} size="lg" />
          <div>
            <span className="admin-card__name" onClick={() => onView(admin)}>
              {admin.firstName} {admin.lastName}
            </span>
            <span className="admin-card__email">{admin.email}</span>
          </div>
        </div>
        <div className="admin-card__header-actions">
          <StatusBadge status={admin.status || "active"} />
          <AdminMenu admin={admin} onView={onView} onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>

      <div className="admin-card__info">
        <div className="admin-card__info-row">
          <span className="admin-card__info-label">Role</span>
          <span className={`role-badge ${getRoleBadgeClass(admin.role)}`}>{admin.role || "Admin"}</span>
        </div>
        <div className="admin-card__info-row">
          <span className="admin-card__info-label">Department</span>
          <span className="admin-card__info-value">{admin.department || "—"}</span>
        </div>
        <div className="admin-card__info-row">
          <span className="admin-card__info-label">Permissions</span>
          <span className="admin-card__info-value">{grantedPerms} / {TOTAL_PERMISSIONS}</span>
        </div>
        <div className="admin-card__info-row">
          <span className="admin-card__info-label">Last Login</span>
          <span className="admin-card__info-value">{formatLastLogin(admin.lastLogin)}</span>
        </div>
      </div>

      <div className="admin-card__footer">
        <button className="app-btn app-btn--ghost app-btn--icon" onClick={() => onView(admin)} title="View">
          <Eye size={16} />
        </button>
        <button className="app-btn app-btn--ghost app-btn--icon" onClick={() => onEdit(admin)} title="Edit">
          <Edit size={16} />
        </button>
        <button className="app-btn app-btn--ghost app-btn--icon" onClick={() => onDelete(admin)} title="Delete">
          <Trash2 size={16} style={{ color: "#e11d48" }} />
        </button>
      </div>
    </div>
  );
}

/* ── Admins Table (list view) ── */
function AdminsTable({ admins, onView, onEdit, onDelete }) {
  return (
    <AppCard className="admins-table-card">
      <AppCardContent style={{ padding: 0 }}>
        <div className="admins-table-wrapper">
          <table className="admins-table">
            <thead>
              <tr>
                <th style={{ minWidth: 240 }}>Admin</th>
                <th>Role</th>
                <th>Department</th>
                <th>Job Title</th>
                <th className="text-center">Permissions</th>
                <th>Last Login</th>
                <th>Status</th>
                <th style={{ width: 50 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin._id}>
                  <td>
                    <div className="admins-table__user">
                      <UserAvatar name={`${admin.firstName} ${admin.lastName}`} size="sm" />
                      <div>
                        <span className="admins-table__user-name" onClick={() => onView(admin)}>
                          {admin.firstName} {admin.lastName}
                        </span>
                        <div className="admins-table__user-email">{admin.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(admin.role)}`}>
                      {admin.role || "Admin"}
                    </span>
                  </td>
                  <td>{admin.department || "—"}</td>
                  <td style={{ fontSize: "0.8125rem" }}>{admin.jobTitle || "—"}</td>
                  <td className="text-center">
                    <span className="admins-table__perm-count">
                      {(admin.permissions || []).length}/{TOTAL_PERMISSIONS}
                    </span>
                  </td>
                  <td className="admins-table__login">{formatLastLogin(admin.lastLogin)}</td>
                  <td><StatusBadge status={admin.status || "active"} /></td>
                  <td>
                    <AdminMenu admin={admin} onView={onView} onEdit={onEdit} onDelete={onDelete} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════ */
/* ═         Main Page         ═ */
/* ══════════════════════════════ */
const AdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();

  // Plan usage limits
  const { usage } = usePlanUsage();
  const adminLimit = isOverLimit(usage?.admins);
  const adminStrictly = isStrictlyOverLimit(usage?.admins);

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/admins/list");
        setAdmins(data.admins || []);
      } catch {
        setToast({ show: true, message: "Failed to load admins.", type: "error" });
      }
      setLoading(false);
    };
    fetchAdmins();
  }, [selectedBranchId]);

  /* Stats */
  const activeCount = admins.filter((a) => a.status === "active").length;
  const inactiveCount = admins.filter((a) => a.status === "inactive").length;
  const departments = [...new Set(admins.map((a) => a.department).filter(Boolean))].length;

  /* Filters */
  const filteredAdmins = useMemo(() => {
    return admins.filter((a) => {
      const fullName = `${a.firstName} ${a.lastName}`.toLowerCase();
      const q = search.toLowerCase();
      const matchesSearch =
        fullName.includes(q) || (a.email || "").toLowerCase().includes(q) || (a.department || "").toLowerCase().includes(q);
      const matchesRole = roleFilter === "all" || a.role === roleFilter;
      const matchesDept = deptFilter === "all" || a.department === deptFilter;
      return matchesSearch && matchesRole && matchesDept;
    });
  }, [admins, search, roleFilter, deptFilter]);

  const handleView = (admin) => navigate(`/admins/${admin._id}`);
  const handleEdit = (admin) => navigate(`/admins/edit/${admin._id}`);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = (admin) => setDeleteTarget(admin);

  const confirmDelete = async () => {
    const admin = deleteTarget;
    setDeleteTarget(null);
    try {
      await apiClient.delete(`/admins/${admin._id}`);
      setAdmins((prev) => prev.filter((a) => a._id !== admin._id));
      setToast({ show: true, message: "Admin deleted!", type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to delete admin.", type: "error" });
    }
  };

  return (
    <div className="admins-page">
      <OverLimitBanner
        resourceName="Admins"
        used={adminLimit.used}
        max={adminLimit.max}
        overLimit={adminLimit.overLimit}
        strictlyOver={adminStrictly.overLimit}
      />
      <PageHeader
        title="Admins"
        description={`Manage ${admins.length} admin accounts and their permissions`}
        icon={Shield}
        actions={
          <AppButton variant="primary" onClick={() => navigate("/admins/add")} disabled={adminLimit.overLimit} title={adminLimit.overLimit ? `Plan limit reached (${adminLimit.used}/${adminLimit.max})` : ''}>
            <Plus size={16} /> Add Admin
          </AppButton>
        }
      />

      {/* Stats row */}
      <div className="admins-stats">
        <StatsCard title="Total Admins" value={admins.length} icon={Shield} iconColor="violet" />
        <StatsCard title="Active" value={activeCount} icon={Check} iconColor="emerald" />
        <StatsCard title="Inactive" value={inactiveCount} icon={Clock} iconColor="amber" />
        <StatsCard title="Departments" value={departments} icon={Building2} iconColor="blue" />
      </div>

      {/* Toolbar */}
      <div className="admins-toolbar">
        <ViewToggle view={view} onViewChange={setView} />
        <div className="admins-search">
          <Search size={16} className="admins-search__icon" />
          <input
            className="admins-search__input"
            placeholder="Search admins..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="admins-filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="admins-filter-select" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="all">All Departments</option>
          {DEPARTMENT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="admins-loading">Loading...</div>
      ) : filteredAdmins.length === 0 ? (
        <EmptyState icon={<Shield size={48} />} title="No admins found" description="Try adjusting your search or filter criteria" />
      ) : view === "grid" ? (
        <div className="admins-grid">
          {filteredAdmins.map((admin) => (
            <AdminCard key={admin._id} admin={admin} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      ) : (
        <AdminsTable admins={filteredAdmins} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AdminsPage;
